import React from 'react';

// ErrorBoundary — catches render/lifecycle throws in its child tree and shows a recoverable message instead of
// letting the throw unmount the whole React root (a blank white screen). React error boundaries MUST be class
// components. Wrap risky subtrees (e.g. DocumentScanner) so one bad render can't white out the entire app.
//
// Props:
//   onReset   — optional; called when the user clicks the reset button (e.g. navigate away / clear launch state).
//   resetLabel— optional button label (default "← Back").
//   label     — optional heading (default "Something went wrong").
//   isDark    — optional theme toggle for the fallback colors.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface for debugging; the fallback UI is what the user sees.
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
    if (this.props.onReset) this.props.onReset();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isDark = this.props.isDark;
    const c = isDark
      ? { bg: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: '#a8b0cc' }
      : { bg: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: '#4a5068' };
    return (
      <div style={{ padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: '700', color: c.text }}>{this.props.label || 'Something went wrong'}</div>
        <div style={{ fontSize: '13px', color: c.muted, maxWidth: '460px' }}>An unexpected error interrupted this screen. Your other work is safe — nothing else was affected.</div>
        <div style={{ fontSize: '11px', fontFamily: 'monospace', color: c.muted, background: c.bg, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '8px 12px', maxWidth: '520px', overflowX: 'auto' }}>
          {String(error?.message || error)}
        </div>
        <button
          onClick={this.reset}
          style={{ marginTop: '4px', padding: '9px 18px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
          {this.props.resetLabel || '← Back'}
        </button>
      </div>
    );
  }
}
